import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {SearchWidgetContentListItem} from '../../../../types/SearchWidgetContentListItem';
import {SearchSuggestItem} from '../../../../types/SearchSuggestItem';
import {SearchWidgetDataItem} from '../../../../types/SearchWidgetDataItem';
import {SearchSuggestPayload} from '../../../../types/SearchSuggestPayload';
import {SearchStateFieldsEnum, SearchSuggestTypesEnum} from '../../../../enums/SearchEnums';
import {ToolsService} from '../../../../services/tools.service';
import {SearchService} from '../../../../services/search/search.service';
import {Subscription} from 'rxjs';
import {SearchWidgetFreeSearchFieldsEnum} from '../../enums/search-widget-free-search-fields.enum';
import {SearchWidgetLabelsEnum} from '../../enums/search-widget-labels.enum';
import {SearchWidgetV3StateService} from '../../services/search-widget-v3-state.service';
import {SearchWidgetV3ManagerService} from '../../services/search-widget-v3-manager.service';
import {PopularItems} from '../../../../types/PopularItems';
import {UrlTermsSymbolsEnum} from '../../../../enums/SeoEnums';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {DefaultValuesService} from '../../../../services/default-values.service';
import {RoutesEnums} from '../../../../enums/RoutesEnums';
import {Router} from '@angular/router';
import {QueryParams} from '../../../../types/QueryParams';
import {EnumsService} from '../../../../services/enums.service';

@Component({
  selector: 'app-search-widget-v3-suggestions',
  templateUrl: './search-widget-v3-suggestions.component.html',
  styleUrls: ['./search-widget-v3-suggestions.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetV3SuggestionsComponent implements OnInit, OnDestroy {
  private static NumberOfSuggestionsToDisplay = 10;
  private static NumberOfPopularToDisplay = 6;
  public suggestTypes = input<string[]>([]);
  public whichIsFocused = input<SearchWidgetFreeSearchFieldsEnum>();
  public popularItems = signal<PopularItems>(null);
  protected readonly SearchWidgetLabelsEnum = SearchWidgetLabelsEnum;
  protected readonly SearchWidgetFreeSearchFieldsEnum = SearchWidgetFreeSearchFieldsEnum;
  protected placeFilteredSuggestItems = signal<Array<SearchSuggestItem>>(null);
  protected freeSearchFilteredSuggestItems = signal<Array<SearchSuggestItem>>(null);
  protected placePopularSuggestItems = computed<Array<SearchWidgetContentListItem>>(() => {
    if (this.popularItems() && this.popularItems().popularPlaces) {
      return this.popularItems()
        .popularPlaces.slice(0, SearchWidgetV3SuggestionsComponent.NumberOfPopularToDisplay)
        .map(
          (item: SearchWidgetDataItem): SearchWidgetContentListItem => ({
            id: item.id,
            name: item.name,
            urlPath: item.urlPath,
            caregiverUnitsCount: item.caregiverUnitsCount,
            columnIndex: 0,
          }),
        );
    }
    return null;
  });
  private urlBuilder = inject(UrlBuilderService);
  private defaults = inject(DefaultValuesService);
  protected freeSearchPopularSuggestItems = computed<Array<SearchWidgetDataItem>>(() => {
    if (this.popularItems() && this.popularItems().popularCaregiverUnitTypes) {
      return this.popularItems()
        .popularCaregiverUnitTypes.slice(0, SearchWidgetV3SuggestionsComponent.NumberOfPopularToDisplay)
        .map((item) => {
          const state = this.defaults.getDefaultSearchState(item.urlPath);
          state.caregiverUnitTypeUrlPath = item.urlPath;
          const baseUrl: string[] = ['/', RoutesEnums.SEARCH];
          const urlPath = this.urlBuilder.CreateSearchResultsRouterLink(state, baseUrl);
          const queryParams = this.urlBuilder.CreateSearchResultsQueryParams(state);
          return {
            ...item,
            urlSymbol: UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE,
            routerLink: urlPath,
            queryParams: queryParams,
          };
        });
    }
    return null;
  });
  private enumsService = inject(EnumsService);
  private subscriptions: Subscription[] = [];
  private searchService = inject(SearchService);
  private stateService = inject(SearchWidgetV3StateService);
  public isLocationSearchVisible = this.stateService.isLocationSearchVisible;
  public isLocationLoading = this.stateService.isLocationLoading;
  public locationGetErrorMessage = this.stateService.locationGetErrorMessage;
  public newFreeSearchFieldValue = this.stateService.newFreeSearchFieldValue;
  public newPlacesFieldValue = this.stateService.newPlacesFieldValue;

  private widgetManagerService = inject(SearchWidgetV3ManagerService);

  constructor(
    private tools: ToolsService,
    private router: Router,
  ) {
    effect(
      () => {
        switch (this.whichIsFocused()) {
          case SearchWidgetFreeSearchFieldsEnum.FREESEARCH:
            if (this.newFreeSearchFieldValue() !== '') {
              this.updateFreeSearchSuggestionsWithQuery(this.newFreeSearchFieldValue());
            } else {
              this.freeSearchFilteredSuggestItems.set(null);
            }
            break;
          case SearchWidgetFreeSearchFieldsEnum.PLACE:
            if (this.newPlacesFieldValue() !== '') {
              this.updatePlaceSuggestionsWithQuery(this.newPlacesFieldValue());
            } else {
              this.placeFilteredSuggestItems.set(null);
            }
            break;
        }
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit() {
    this.subscriptions.push(
      this.searchService.getPopularItems().subscribe(
        (result: PopularItems) => {
          this.popularItems.set(result);
        },
        (error) => console.error(error),
      ),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  protected onFreeSearchPopularOptionClick(option: SearchWidgetDataItem) {
    this.router.navigate(option.routerLink, {queryParams: option.queryParams});
    this.closePanel();
  }

  protected searchByLocationClick(): void {
    this.stateService.updateIsLocationLoading(true);
    this.stateService.updateLocationGetErrorMessage(null);
    this.widgetManagerService.getUserLocation();
  }

  protected onPlaceOptionClick(placeName: string) {
    this.stateService.updateNewPlacesFieldValue(placeName);
    this.stateService.updatePlacesFieldValue(placeName);
    this.stateService.updateShouldFocusPlacesField(false);
    setTimeout(() => {
      this.stateService.updateShouldFocusPlacesField(true);
      this.widgetManagerService.onSearchButtonClick();
    }, 0);
  }

  protected onFreeSearchOptionClick(item: SearchSuggestItem) {
    let searchState = this.tools.clone(this.defaults.getDefaultSearchState());
    let navigateToSearchResults = true;
    searchState.freeSearchTerm = '';
    searchState.symptomUrlPath = '';
    searchState.careServiceTypeUrlPath = '';
    searchState.caregiverUnitTypeUrlPath = '';

    item.searchValue.forEach((item) => {
      if (item.type === SearchSuggestTypesEnum.CAREPROVIDER_UNIT) {
        navigateToSearchResults = false;
        this.redirectToCareproviderUnitPageWhenSelected(item.urlPath);
      } else if (item.type === SearchSuggestTypesEnum.CAREPROVIDER) {
        navigateToSearchResults = false;
        this.redirectToCareproviderLandingPageWhenSelected(item.urlPath);
      } else {
        const enumKey: string = this.enumsService.getEnumKeyByEnumValue(SearchSuggestTypesEnum, item.type);
        searchState[SearchStateFieldsEnum[enumKey]] = item.urlPath;
      }
    });

    if (navigateToSearchResults) {
      const baseUrl: string[] = ['/', RoutesEnums.SEARCH];
      const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(searchState, baseUrl);
      const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(searchState);
      this.router.navigate(routerLink, {queryParams});
    }
    this.closePanel();
  }

  private redirectToCareproviderUnitPageWhenSelected(urlPath: string): void {
    const routerLink: string[] = this.urlBuilder.CreateCareproviderUnitRouterLinkFromString(urlPath);
    this.router.navigate(routerLink, {});
  }

  private redirectToCareproviderLandingPageWhenSelected(urlPath: string): void {
    const routerLink: string[] = this.urlBuilder.CreateCareproviderLandingPageRouterLinkFromString(urlPath);
    this.router.navigate(routerLink, {});
  }

  private updateFreeSearchSuggestionsWithQuery(term: string): void {
    if (!this.tools.isNil(term) && term.length > 0) {
      const suggestPayload: SearchSuggestPayload = {
        term: encodeURIComponent(this.tools.clone(term).trim()),
        size: SearchWidgetV3SuggestionsComponent.NumberOfSuggestionsToDisplay,
      };
      if (this.suggestTypes() && this.suggestTypes().length > 0) {
        suggestPayload.types = this.suggestTypes();
      }
      this.subscriptions.push(
        this.searchService.freeSearchSuggest(suggestPayload).subscribe(
          (result) => {
            this.freeSearchFilteredSuggestItems.set(result);
          },
          (error) => console.error(error),
        ),
      );
    }
  }

  private updatePlaceSuggestionsWithQuery(term: string): void {
    if (!this.tools.isNil(term) && term.length > 0) {
      const suggestPayload: SearchSuggestPayload = {
        term: encodeURIComponent(this.tools.clone(term).trim()),
        size: SearchWidgetV3SuggestionsComponent.NumberOfSuggestionsToDisplay,
        types: [SearchSuggestTypesEnum.PLACE],
      };
      this.subscriptions.push(
        this.searchService.freeSearchSuggest(suggestPayload).subscribe(
          (result) => this.processSuggestData(result),
          (error) => console.error(error),
        ),
      );
    }
  }

  private processSuggestData(suggestions: SearchSuggestItem[]): void {
    const suggestionsWithLabels: SearchSuggestItem[] = [];
    suggestions.forEach((item) => {
      item.suggestLabel = this.widgetManagerService.getSuggestTypeLabel(item);
      suggestionsWithLabels.push(item);
    });
    this.placeFilteredSuggestItems.set(suggestionsWithLabels);
  }

  private closePanel(): void {
    this.stateService.updateIsSearchWidgetExpanded(false);
  }
}
